import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { usePlanningWeek, useMissionTypeSettings } from "@/hooks/use-planning";
import { useProfiles } from "@/hooks/use-profiles";
import { useRobots, useVehicles } from "@/hooks/use-inventory";
import { useAgences } from "@/hooks/use-agences";
import { MISSION_PALETTE } from "@shared/missionPalette";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, Users, Bot, Truck } from "lucide-react";
import type { PlanningMission } from "@/hooks/use-planning";
import type { Profile } from "@shared/schema";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toISO(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatWeekLabel(monday: Date): string {
  const sunday = addDays(monday, 6);
  const months = ["jan", "fév", "mar", "avr", "mai", "juin", "juil", "août", "sep", "oct", "nov", "déc"];
  const mD = monday.getDate();
  const mM = months[monday.getMonth()];
  const sD = sunday.getDate();
  const sM = months[sunday.getMonth()];
  const y = sunday.getFullYear();
  if (monday.getMonth() === sunday.getMonth()) {
    return `Lun ${mD} – Dim ${sD} ${sM} ${y}`;
  }
  return `Lun ${mD} ${mM} – Dim ${sD} ${sM} ${y}`;
}

function dayIndex(dateStr: string, monday: Date): number {
  const d = new Date(dateStr + "T12:00:00");
  const diff = Math.round((d.getTime() - monday.getTime()) / 86400000);
  return Math.max(0, Math.min(6, diff));
}

// ── Helpers TZ-safe : jour calendaire Europe/Paris, immunisé DST ──────────────

/** Retourne {y, m, d} de la date en heure Europe/Paris. */
function parisYMD(date: Date): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  return {
    y: parseInt(parts.find(p => p.type === "year")!.value, 10),
    m: parseInt(parts.find(p => p.type === "month")!.value, 10),
    d: parseInt(parts.find(p => p.type === "day")!.value, 10),
  };
}

/** Nombre entier de jours entre deux dates calendaires (arithmétique pure via Date.UTC). */
function dayOffset(a: { y: number; m: number; d: number }, b: { y: number; m: number; d: number }): number {
  return Math.round((Date.UTC(a.y, a.m - 1, a.d) - Date.UTC(b.y, b.m - 1, b.d)) / 86400000);
}

/**
 * Calcule colStart/colEnd en jours-colonne depuis le lundi de la semaine.
 * Référentiel : jour calendaire Europe/Paris — résistant aux décalages DST
 * et aux conventions de stockage (end=start, end=23:59:59.999, end=midnight exclusif).
 */
function clampToWeek(start: Date, end: Date, monday: Date): { colStart: number; colEnd: number } {
  const mondayYMD = parisYMD(monday);
  const colStart = dayOffset(parisYMD(start), mondayYMD);

  // Détection borne exclusive : si end tombe sur une frontière de jour Paris (minuit)
  // ET end > start → reculer d'1ms pour obtenir le dernier jour couvert.
  const endYMD      = parisYMD(end);
  const endMinus1YMD = parisYMD(new Date(end.getTime() - 1));
  const endIsBoundary = end.getTime() > start.getTime() && (
    endYMD.y !== endMinus1YMD.y || endYMD.m !== endMinus1YMD.m || endYMD.d !== endMinus1YMD.d
  );
  const endForDay = endIsBoundary ? new Date(end.getTime() - 1) : end;

  const colEndIncl = dayOffset(parisYMD(endForDay), mondayYMD);
  const colEnd = Math.max(colStart, colEndIncl) + 1;

  return {
    colStart: Math.max(0, Math.min(6, colStart)),
    colEnd:   Math.max(colStart + 1, Math.min(7, colEnd)),
  };
}

type Axis = "technicians" | "robots" | "vehicles";

// ── Mission pill ──────────────────────────────────────────────────────────────

interface MissionPillProps {
  mission: PlanningMission;
  colStart: number;
  colEnd: number;
  row: number;
  typeColor: { bg: string; border: string; text: string };
  onClick: () => void;
}

function MissionPill({ mission, colStart, colEnd, row, typeColor, onClick }: MissionPillProps) {
  const label = mission.heure_debut
    ? `${mission.heure_debut} ${mission.titre}`
    : `Journée · ${mission.titre}`;

  return (
    <div
      className="absolute rounded-md px-2 py-0.5 text-xs font-medium cursor-pointer hover:brightness-95 transition-all overflow-hidden whitespace-nowrap text-ellipsis z-10 border"
      style={{
        left:  `calc(${(colStart / 7) * 100}% + 1px)`,
        width: `calc(${((colEnd - colStart) / 7) * 100}% - 2px)`,
        top:   `${2 + row * 24}px`,
        height: "22px",
        backgroundColor: typeColor.bg,
        color:           typeColor.text,
        borderColor:     typeColor.border,
      }}
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={mission.titre}
      data-testid={`pill-mission-${mission.id}`}
    >
      {label}
    </div>
  );
}

// ── Grid ──────────────────────────────────────────────────────────────────────

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

interface GridProps {
  rows: { id: string; label: string; sublabel?: string }[];
  missions: PlanningMission[];
  monday: Date;
  typeSettings: Record<string, { bg: string; border: string; text: string }>;
  onCellClick: (resourceId: string, date: Date) => void;
  onMissionClick: (mission: PlanningMission) => void;
}

function PlanningGrid({ rows, missions, monday, typeSettings, onCellClick, onMissionClick }: GridProps) {
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

  return (
    <div className="overflow-auto">
      <div className="min-w-[700px]">
        {/* Header */}
        <div className="grid grid-cols-[180px_repeat(7,1fr)] border-b bg-muted/30">
          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-r">Ressource</div>
          {weekDays.map((day, i) => {
            const today = toISO(new Date()) === toISO(day);
            return (
              <div
                key={i}
                className={`px-1 py-2 text-center text-xs font-semibold border-r last:border-r-0 ${today ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}
              >
                <div>{DAY_LABELS[i]}</div>
                <div className={`text-base font-bold mt-0.5 ${today ? "text-primary" : "text-foreground"}`}>{day.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* Rows */}
        {rows.length === 0 && (
          <div className="py-16 text-center text-sm text-muted-foreground">Aucune ressource à afficher.</div>
        )}
        {rows.map(row => {
          // Missions for this resource
          const rowMissions = missions.filter(m => {
            const ids = [...m.technicien_ids, ...m.commercial_ids];
            const robotIds = m.robots.map(r => r.id);
            const vehicleIds = m.vehicules.map(v => v.id);
            return ids.includes(row.id) || robotIds.includes(row.id) || vehicleIds.includes(row.id);
          });

          // Deduplicate: a person appearing in both technicien_ids + commercial_ids → 1 pill
          const seenMissionIds = new Set<string>();
          const uniqueMissions = rowMissions.filter(m => {
            if (seenMissionIds.has(m.id)) return false;
            seenMissionIds.add(m.id);
            return true;
          });

          // Sort: heure_debut asc, null last
          uniqueMissions.sort((a, b) => {
            if (!a.heure_debut && !b.heure_debut) return 0;
            if (!a.heure_debut) return 1;
            if (!b.heure_debut) return -1;
            return a.heure_debut.localeCompare(b.heure_debut);
          });

          // Compute lane assignments to handle overlapping missions
          const lanes: PlanningMission[][] = [];
          for (const m of uniqueMissions) {
            const mStart = new Date(m.start_date);
            const mEnd   = new Date(m.end_date);
            const { colStart, colEnd } = clampToWeek(mStart, mEnd, monday);
            let placed = false;
            for (const lane of lanes) {
              const lastInLane = lane[lane.length - 1];
              const lastEnd = new Date(lastInLane.end_date);
              const { colEnd: lastColEnd } = clampToWeek(new Date(lastInLane.start_date), lastEnd, monday);
              if (colStart >= lastColEnd) {
                lane.push(m);
                placed = true;
                break;
              }
            }
            if (!placed) lanes.push([m]);
          }

          const rowHeight = Math.max(40, lanes.length * 24 + 6);

          return (
            <div key={row.id} className="grid grid-cols-[180px_repeat(7,1fr)] border-b hover:bg-muted/10">
              {/* Resource label */}
              <div className="px-3 py-2 border-r flex flex-col justify-center">
                <span className="text-sm font-medium truncate">{row.label}</span>
                {row.sublabel && <span className="text-xs text-muted-foreground truncate">{row.sublabel}</span>}
              </div>

              {/* Day cells container (relative positioned for absolute pills) */}
              <div
                className="col-span-7 relative"
                style={{ height: `${rowHeight}px` }}
              >
                {/* Clickable day cells */}
                {weekDays.map((day, di) => (
                  <div
                    key={di}
                    className="absolute top-0 h-full border-r last:border-r-0 hover:bg-primary/5 cursor-pointer transition-colors"
                    style={{ left: `${(di / 7) * 100}%`, width: `${(1 / 7) * 100}%` }}
                    onClick={() => onCellClick(row.id, day)}
                    data-testid={`cell-${row.id}-day${di}`}
                  />
                ))}

                {/* Mission pills (absolute, spans columns) */}
                {lanes.map((lane, laneIdx) =>
                  lane.map(m => {
                    const { colStart, colEnd } = clampToWeek(new Date(m.start_date), new Date(m.end_date), monday);
                    const color = typeSettings[m.type] ?? MISSION_PALETTE.gray;
                    return (
                      <MissionPill
                        key={`${m.id}-${laneIdx}`}
                        mission={m}
                        colStart={colStart}
                        colEnd={colEnd}
                        row={laneIdx}
                        typeColor={color}
                        onClick={() => onMissionClick(m)}
                      />
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PlanningSemainePage() {
  const [, navigate] = useLocation();
  const [monday, setMonday] = useState<Date>(() => getMondayOfWeek(new Date()));
  const [axis, setAxis] = useState<Axis>("technicians");
  const [agencyFilter, setAgencyFilter] = useState<"ALL" | "IDF" | "SUD">("ALL");
  const [categoryFilter, setCategoryFilter] = useState<"ALL" | "technicien" | "commercial">("ALL");
  const [hideEmpty, setHideEmpty] = useState(true);

  const mondayStr = toISO(monday);

  const { data: weekMissions = [], isLoading: loadingMissions } = usePlanningWeek(mondayStr);
  const { data: typeSettingsArr = [] }   = useMissionTypeSettings();
  const { data: profiles = [] }          = useProfiles();
  const { data: allRobots = [] }         = useRobots();
  const { data: allVehicles = [] }       = useVehicles();
  const { data: agences = [] }            = useAgences();

  // Build palette map from settings
  const typeSettings = useMemo(() => {
    const map: Record<string, { bg: string; border: string; text: string }> = {};
    typeSettingsArr.forEach(s => {
      map[s.typeKey] = MISSION_PALETTE[s.couleur] ?? MISSION_PALETTE.gray;
    });
    return map;
  }, [typeSettingsArr]);

  // ── Rows by axis ──────────────────────────────────────────────────────────
  const assignedProfileIds = useMemo(
    () => new Set(weekMissions.flatMap(m => [...m.technicien_ids, ...m.commercial_ids])),
    [weekMissions],
  );
  const rows = useMemo(() => {
    if (axis === "technicians") {
      // Merge technicians + commercials, deduplicate by id
      const seen = new Set<string>();
      const all: Profile[] = [];
      (profiles as Profile[]).filter(p => p.isActive).forEach(p => {
        if (!seen.has(p.id)) { seen.add(p.id); all.push(p); }
      });
      const agencyId = agencyFilter === "ALL" ? null : agences.find(a => a.code === agencyFilter)?.id;
      const filtered = all.filter(p => {
        const agencyMatches = agencyFilter === "ALL" || (Boolean(agencyId) && (p as any).agenceId === agencyId);
        const categoryMatches = categoryFilter === "ALL" || p.role === categoryFilter;
        return ((agencyMatches && categoryMatches) || assignedProfileIds.has(p.id))
          && (!hideEmpty || assignedProfileIds.has(p.id));
      });
      return filtered.map(p => ({
        id: p.id,
        label: `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.email || p.id,
        sublabel: p.role,
      }));
    }

    if (axis === "robots") {
      // Only robots that have at least 1 mission this week
      const robotIdsInMissions = new Set(weekMissions.flatMap(m => m.robots.map(r => r.id)));
      return (allRobots as any[])
        .filter(r => robotIdsInMissions.has(r.id))
        .map(r => ({
          id: r.id,
          label: r.nickname || r.modelName || r.sku,
          sublabel: r.serialNumber,
        }));
    }

    // axis === "vehicles"
    return (allVehicles as any[]).filter(v =>
      agencyFilter === "ALL" || !v.agenceId || (Boolean(agences.find(a => a.code === agencyFilter)?.id) && v.agenceId === agences.find(a => a.code === agencyFilter)?.id)
    ).map(v => ({
      id: v.id,
      label: v.brandModel || v.vehicleType,
      sublabel: v.nature === "transport" ? "Mode de transport" : v.licensePlate,
    }));
  }, [axis, profiles, allRobots, allVehicles, weekMissions, agencyFilter, categoryFilter, hideEmpty, assignedProfileIds, agences]);
  const hiddenProfileCount = useMemo(() => {
    if (axis !== "technicians" || !hideEmpty) return 0;
    const activeProfiles = (profiles as Profile[]).filter(p => p.isActive);
    const agencyId = agencyFilter === "ALL" ? null : agences.find(a => a.code === agencyFilter)?.id;
    const matchingUnassignedIds = new Set(activeProfiles.filter(p => {
      if (assignedProfileIds.has(p.id)) return false;
      const agencyMatches = agencyFilter === "ALL" || (Boolean(agencyId) && (p as any).agenceId === agencyId);
      const categoryMatches = categoryFilter === "ALL" || p.role === categoryFilter;
      return agencyMatches && categoryMatches;
    }).map(p => p.id));
    return matchingUnassignedIds.size;
  }, [axis, hideEmpty, profiles, assignedProfileIds, agencyFilter, categoryFilter, agences]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleCellClick = (resourceId: string, date: Date) => {
    const dateStr = toISO(date);
    const params = new URLSearchParams();
    params.set("planningDate", dateStr);
    params.set("openCreate", "1");
    if (axis === "technicians") params.set("technicianId", resourceId);
    if (axis === "robots")      params.set("planningRobotId", resourceId);
    if (axis === "vehicles")    params.set("planningVehicleId", resourceId);
    navigate(`/missions?${params.toString()}`);
  };

  const handleMissionClick = (mission: PlanningMission) => {
    navigate(`/missions?openDetail=${mission.id}`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMonday(prev => addDays(prev, -7))}
              data-testid="button-prev-week"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMonday(getMondayOfWeek(new Date()))}
              data-testid="button-today"
            >
              Aujourd'hui
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMonday(prev => addDays(prev, 7))}
              data-testid="button-next-week"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <h2 className="text-sm font-semibold">{formatWeekLabel(monday)}</h2>
        </div>

        <div className="flex items-center gap-2">
          <Tabs value={axis} onValueChange={v => setAxis(v as Axis)}>
            <TabsList>
            <TabsTrigger value="technicians" data-testid="tab-technicians">
              <Users className="w-4 h-4 mr-1.5" />
              Techniciens
            </TabsTrigger>
            <TabsTrigger value="robots" data-testid="tab-robots">
              <Bot className="w-4 h-4 mr-1.5" />
              Robots
            </TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">
              <Truck className="w-4 h-4 mr-1.5" />
              Véhicules
            </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-muted/20 text-xs">
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Agence</span>
          <select className="h-8 rounded-md border bg-background px-2 text-xs" value={agencyFilter} onChange={e => setAgencyFilter(e.target.value as typeof agencyFilter)} data-testid="select-planning-agency">
            <option value="ALL">Toutes</option><option value="IDF">IDF</option><option value="SUD">SUD</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Catégorie</span>
          <select className="h-8 rounded-md border bg-background px-2 text-xs" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as typeof categoryFilter)} disabled={axis !== "technicians"} data-testid="select-planning-category">
            <option value="ALL">Toutes</option><option value="technicien">Techniciens</option><option value="commercial">Commerciaux</option>
          </select>
        </label>
        <label className="flex items-center gap-2 h-8 rounded-md border bg-background px-2">
          <Switch checked={hideEmpty} onCheckedChange={setHideEmpty} disabled={axis !== "technicians"} data-testid="switch-hide-empty" />
          Masquer les lignes vides
        </label>
        {axis === "technicians" && hideEmpty && <span className="text-muted-foreground">{hiddenProfileCount} ligne{hiddenProfileCount > 1 ? "s" : ""} masquée{hiddenProfileCount > 1 ? "s" : ""}</span>}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-4">
        {loadingMissions ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <>
          {rows.length === 0 && axis !== "robots" && (
            <div className="mb-3 rounded-md border border-dashed bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              Aucun résultat avec ces filtres. Élargissez l’agence, la catégorie ou affichez les ressources vides.
            </div>
          )}
          <PlanningGrid
            rows={rows}
            missions={weekMissions}
            monday={monday}
            typeSettings={typeSettings}
            onCellClick={handleCellClick}
            onMissionClick={handleMissionClick}
          />
          </>
        )}
      </div>
    </div>
  );
}
